// src/client/game/input/VirtualSwipe.ts

import Phaser from "phaser";
import { Direction } from "../core/model";

type SwipeState = {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  lastDir: Direction | null;
  isTapCandidate: boolean;
};

export class VirtualSwipe {
  public isMobileLayout = false;

  private swipeZone?: Phaser.GameObjects.Zone;
  private outlineGraphics?: Phaser.GameObjects.Graphics;
  private trailGraphics?: Phaser.GameObjects.Graphics;
  private hintIcon?: Phaser.GameObjects.Text;

  private state: SwipeState | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly canHandleInput: () => boolean,
    private readonly canFire: () => boolean,
    private readonly onDirection: (dir: Direction) => void,
    private readonly onFire: () => void
  ) {}

  create(): void {
    const width = this.scene.scale.width-5;
    const height = this.scene.scale.height - 10;

    // Same "mobile-ish" layout heuristic as others
    const isMobileLayout = height > width || width < 640;
    this.isMobileLayout = isMobileLayout;
    if (!isMobileLayout) return;

    // Bottom slice reserved for swipe / tap input
    const controlsTop = height * 0.6;
    const controlsHeight = height - controlsTop;

    // Lightened swipe area + outline
    const g = this.scene.add.graphics();
    g.fillStyle(0x444444, 0.35);
    g.fillRect(5, controlsTop, width, controlsHeight);
    g.lineStyle(2, 0xaaaaaa, 1);
    g.strokeRect(0, controlsTop, width, controlsHeight);
    g.setDepth(498).setScrollFactor(0);
    this.outlineGraphics = g;

    // Trail graphics (swipe path)
    const trail = this.scene.add.graphics();
    trail.setDepth(500).setScrollFactor(0);
    this.trailGraphics = trail;

    // Finger swipe hint icon (light gray)
    this.hintIcon = this.scene.add
      .text(
        width / 2,
        controlsTop + controlsHeight / 2,
        "☝  swipe to move, tap to fire",
        {
          fontFamily: "monospace",
          fontSize: "16px",
          color: "#aaaaaa",
        }
      )
      .setOrigin(0.5)
      .setDepth(501)
      .setScrollFactor(0);

    // Invisible hit zone for swipes / taps
    const zone = this.scene.add.zone(
      width / 2,
      controlsTop + controlsHeight / 2,
      width,
      controlsHeight
    );
    zone.setInteractive({ useHandCursor: true });
    zone.setDepth(499).setScrollFactor(0);
    this.swipeZone = zone;

    zone.on("pointerdown", this.handlePointerDown, this);
    zone.on("pointermove", this.handlePointerMove, this);
    zone.on("pointerup", this.handlePointerUp, this);
    zone.on("pointerupoutside", this.handlePointerUp, this);
    zone.on("pointerout", this.handlePointerUp, this);
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.canHandleInput()) return;
    if (this.state !== null) return; // track only one touch

    // Clear previous trail and start a new path
    if (this.trailGraphics) {
      this.trailGraphics.clear();
      this.trailGraphics.lineStyle(3, 0xcccccc, 0.8);
      this.trailGraphics.beginPath();
      this.trailGraphics.moveTo(pointer.x, pointer.y);
    }

    // Fade the hint a bit once the player interacts
    if (this.hintIcon) {
      this.hintIcon.setAlpha(0.35);
    }

    this.state = {
      pointerId: pointer.id,
      startX: pointer.x,
      startY: pointer.y,
      lastX: pointer.x,
      lastY: pointer.y,
      lastDir: null,
      isTapCandidate: true,
    };
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.state || pointer.id !== this.state.pointerId) return;
    if (!this.canHandleInput()) return;

    const TAP_THRESHOLD = 18; // px – beyond this it's a swipe
    const SEGMENT_THRESHOLD = 24; // px – distance before we consider a new direction segment

    const totalDx = pointer.x - this.state.startX;
    const totalDy = pointer.y - this.state.startY;
    const totalDist = Math.hypot(totalDx, totalDy);
    if (totalDist > TAP_THRESHOLD) {
      this.state.isTapCandidate = false;
    }

    // Draw swipe trail
    if (this.trailGraphics) {
      this.trailGraphics.lineTo(pointer.x, pointer.y);
      this.trailGraphics.strokePath();
      this.trailGraphics.beginPath();
      this.trailGraphics.moveTo(pointer.x, pointer.y);
    }

    // L-shape / multi-segment detection: look at movement since last direction
    const segDx = pointer.x - this.state.lastX;
    const segDy = pointer.y - this.state.lastY;
    const segDist = Math.hypot(segDx, segDy);
    if (segDist < SEGMENT_THRESHOLD) return;

    const newDir = this.vectorToDir(segDx, segDy);
    if (!newDir) return;

    // Avoid instant 180° reversals from one swipe segment
    if (
      this.state.lastDir &&
      this.state.lastDir.dx === -newDir.dx &&
      this.state.lastDir.dy === -newDir.dy
    ) {
      return;
    }

    // Emit new direction; this lets an L-shaped swipe produce
    // two turns (e.g., UP then RIGHT) safely.
    this.onDirection(newDir);
    this.state.lastDir = newDir;
    this.state.lastX = pointer.x;
    this.state.lastY = pointer.y;
  }

  private handlePointerUp(pointer: Phaser.Input.Pointer): void {
    if (!this.state || pointer.id !== this.state.pointerId) return;

    const TAP_THRESHOLD = 18;

    const totalDx = pointer.x - this.state.startX;
    const totalDy = pointer.y - this.state.startY;
    const totalDist = Math.hypot(totalDx, totalDy);

    // Short movement => treat as tap (fire)
    if (this.state.isTapCandidate && totalDist <= TAP_THRESHOLD) {
      if (this.canFire()) {
        this.onFire();
      }
    }

    // Clear trail when swipe ends
    if (this.trailGraphics) {
      this.trailGraphics.clear();
    }

    this.state = null;
  }

  private vectorToDir(dx: number, dy: number): Direction | null {
    const MIN_DIST = 6;
    if (Math.hypot(dx, dy) < MIN_DIST) return null;

    if (Math.abs(dx) > Math.abs(dy)) {
      // Horizontal swipe
      return { dx: dx > 0 ? 1 : -1, dy: 0 };
    } else {
      // Vertical swipe
      return { dx: 0, dy: dy > 0 ? 1 : -1 };
    }
  }

  destroy(): void {
    if (!this.isMobileLayout) return;

    if (this.swipeZone) {
      this.swipeZone.off("pointerdown", this.handlePointerDown, this);
      this.swipeZone.off("pointermove", this.handlePointerMove, this);
      this.swipeZone.off("pointerup", this.handlePointerUp, this);
      this.swipeZone.off("pointerupoutside", this.handlePointerUp, this);
      this.swipeZone.off("pointerout", this.handlePointerUp, this);
      this.swipeZone.destroy();
      this.swipeZone = undefined;
    }

    this.outlineGraphics?.destroy();
    this.outlineGraphics = undefined;

    this.trailGraphics?.destroy();
    this.trailGraphics = undefined;

    this.hintIcon?.destroy();
    this.hintIcon = undefined;

    this.state = null;
  }
}
